/// The /towerlog page — a public, celebratory stats page. Cumulative-only:
/// counters that only go up, weekly recaps, milestone posts. No rates, no
/// funnels, no conversion numbers anywhere on this surface.
///
/// Two independent data sources render here:
///   - The hero counter grid comes from `getTowerLog` (a public Cloud
///     Function, no auth) via `fetchTowerLog()`.
///   - The entry feed below it is ordinary `webDevPosts` docs tagged
///     `category: "towerlog"`, rendered with the SAME board components used
///     on /talktothedeveloper (`DevBlogPostCard` / `DevBlogPostDetail`), so
///     votes and comments work exactly like they do on the board — no new
///     engagement code, just a category filter.

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { logTowerLogComment, logTowerLogVote, logTowerLogViewed } from '../analytics/AnalyticsManager'
import {
  getMyVote,
  setVote,
  subscribeTowerLogPosts,
  voteAggregateDeltas,
  type DevPost,
  type VoteValue,
} from '../data/devBlog'
import {
  fetchTowerLog,
  formatChangeSince,
  formatCounterDelta,
  type CounterValue,
  type TowerLogComingSoonBadge,
  type TowerLogPayload,
  type TowerLogWeeklyChange,
  visibleComingSoonBadges,
} from '../data/towerLog'
import { useProfileStore } from '../state/profileStore'
import { formatCounterValue } from '../util/formatCounter'
import { DevBlogPostCard } from './DevBlogPostCard'
import { DevBlogPostDetail } from './DevBlogPostDetail'

const FEED_LIMIT = 100
const PASSENGERS_COUNTER_ID = 'passengersDelivered'

type View = { kind: 'list' } | { kind: 'post'; postId: string }

export function TowerLogPage(props: { onHome: () => void }) {
  const profileState = useProfileStore((s) => s.state)
  const user = profileState.kind === 'ready' ? profileState.user : null

  const [view, setView] = useState<View>({ kind: 'list' })
  const [payload, setPayload] = useState<TowerLogPayload | null>(null) // null = loading
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [posts, setPosts] = useState<DevPost[] | null>(null)
  const [postsErr, setPostsErr] = useState<string | null>(null)
  const [myVotes, setMyVotes] = useState<Record<string, VoteValue | null>>({})
  const [scoreAdj, setScoreAdj] = useState<Record<string, number>>({})

  useEffect(() => {
    logTowerLogViewed()
  }, [])

  useEffect(() => {
    let cancelled = false
    void fetchTowerLog()
      .then((p) => {
        if (!cancelled) setPayload(p)
      })
      .catch((e) => {
        if (!cancelled) setLoadErr(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (view.kind !== 'list') return
    setPosts(null)
    setPostsErr(null)
    return subscribeTowerLogPosts({ limit: FEED_LIMIT, onChange: setPosts, onError: (e) => setPostsErr(e.message) })
  }, [view.kind])

  const postIdsKey = posts?.map((p) => p.id).join(',')
  useEffect(() => {
    if (!user || !posts) return
    let cancelled = false
    void Promise.all(
      posts.map(async (p) => [p.id, await getMyVote({ postId: p.id, voterUid: user.uid }).catch(() => null)] as const),
    ).then((pairs) => {
      if (cancelled) return
      setMyVotes((prev) => {
        const next = { ...prev }
        for (const [id, v] of pairs) next[id] = v
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [user?.uid, postIdsKey])

  const votePost = async (postId: string, next: VoteValue | null) => {
    if (!user) return
    const prev = myVotes[postId] ?? null
    const d = voteAggregateDeltas(prev, next)
    setMyVotes((m) => ({ ...m, [postId]: next }))
    setScoreAdj((m) => ({ ...m, [postId]: (m[postId] ?? 0) + d.score }))
    try {
      await setVote({ postId, voterUid: user.uid, value: next })
      logTowerLogVote()
    } catch {
      setMyVotes((m) => ({ ...m, [postId]: prev }))
      setScoreAdj((m) => ({ ...m, [postId]: (m[postId] ?? 0) - d.score }))
    }
  }

  if (view.kind === 'post' && user) {
    return (
      <TowerLogShell onHome={props.onHome}>
        <DevBlogPostDetail
          postId={view.postId}
          user={user}
          onBack={() => setView({ kind: 'list' })}
          onVoteCast={logTowerLogVote}
          onCommentPosted={logTowerLogComment}
        />
      </TowerLogShell>
    )
  }

  return (
    <TowerLogShell onHome={props.onHome}>
      <p className="text-sm leading-relaxed text-slate-400">
        Every shift ever worked, counted up in one place. No rates, no funnels, just the running total. Vote and reply
        on any entry below exactly like the developer board.
      </p>

      {loadErr && (
        <div className="rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">
          Couldn't load the counters: {loadErr}
        </div>
      )}
      {payload === null && !loadErr && <p className="text-sm text-slate-400">Loading the log…</p>}
      {payload && !payload.available && (
        <p className="rounded-md bg-slate-900/60 px-3 py-2 text-xs text-slate-400 ring-1 ring-white/10">
          {payload.reason ?? 'The Tower Log is warming up. Check back soon.'}
        </p>
      )}
      {payload?.available && <CounterGrid payload={payload} />}

      <div className="pt-2">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Log entries</h2>
        {postsErr && (
          <div className="mb-2 rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">{postsErr}</div>
        )}
        {posts === null && !postsErr && <p className="text-sm text-slate-400">Loading…</p>}
        {posts && posts.length === 0 && (
          <p className="text-sm text-slate-500">No entries yet. The first weekly recap posts here.</p>
        )}
        {posts && posts.length > 0 && (
          <div className="space-y-2">
            {posts.map((p) => (
              <DevBlogPostCard
                key={p.id}
                post={{ ...p, score: p.score + (scoreAdj[p.id] ?? 0) }}
                myVote={myVotes[p.id] ?? null}
                voteDisabled={!user || !!p.archivedAt}
                onVote={(next) => votePost(p.id, next)}
                onOpen={() => setView({ kind: 'post', postId: p.id })}
              />
            ))}
          </div>
        )}
      </div>

      <p className="pt-4 text-center text-xs italic text-slate-500">
        Counted from every shift ever worked. Numbers only go up.
      </p>
    </TowerLogShell>
  )
}

function CounterGrid(props: { payload: TowerLogPayload }) {
  const { counters, labels, comingSoon, weeklyChange } = props.payload
  if (!counters || !labels) return null
  const ids = Object.keys(counters).filter((id) => id !== PASSENGERS_COUNTER_ID)
  const passengers = counters[PASSENGERS_COUNTER_ID]
  // The window is stated ONCE for the whole grid rather than eighteen times
  // under eighteen tiles, and only when something actually moved.
  const changedSince =
    weeklyChange && Object.keys(weeklyChange.deltas).length > 0 ? formatChangeSince(weeklyChange.since) : null

  return (
    <div className="space-y-3">
      {(props.payload.updatedAt || changedSince) && (
        <p className="text-[11px] uppercase tracking-wider text-slate-500">
          {props.payload.updatedAt &&
            `Updated ${new Date(props.payload.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
          {props.payload.updatedAt && changedSince && <span className="text-slate-600"> · </span>}
          {changedSince && <span className="text-emerald-300/60">change since {changedSince}</span>}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {ids.map((id) => (
          <CounterTile
            key={id}
            counterId={id}
            label={labels[id] ?? id}
            value={counters[id] ?? 'coming_soon'}
            weeklyChange={weeklyChange}
          />
        ))}
        {typeof passengers === 'number' && (
          <CounterTile
            counterId={PASSENGERS_COUNTER_ID}
            label={labels[PASSENGERS_COUNTER_ID] ?? 'Passengers safely delivered'}
            value={passengers}
            weeklyChange={weeklyChange}
            estimated
          />
        )}
        {visibleComingSoonBadges(comingSoon, counters).map((b: TowerLogComingSoonBadge) => (
          <ComingSoonTile key={b.id} label={b.label} />
        ))}
      </div>
    </div>
  )
}

function CounterTile(props: {
  counterId: string
  label: string
  value: CounterValue
  weeklyChange?: TowerLogWeeklyChange | null
  estimated?: boolean
}) {
  if (props.value === 'coming_soon') {
    return <ComingSoonTile label={props.label} />
  }
  // Absent (not zero) when this counter held still — a grid of "+0" would
  // make a celebratory page look stalled.
  const delta = formatCounterDelta(props.weeklyChange, props.counterId)
  return (
    <div className="rounded-xl bg-white/[0.04] p-3 text-center ring-1 ring-white/10">
      <div className="text-2xl font-black tabular-nums tracking-tight text-white sm:text-3xl">
        {props.estimated && <span className="text-white/50">~</span>}
        {formatCounterValue(props.value)}
      </div>
      <div className="mt-1 text-[11px] leading-tight text-white/60">
        {props.label}
        {props.estimated && <span className="block text-white/35">estimated</span>}
      </div>
      {delta && <div className="mt-1 text-[11px] font-semibold tabular-nums text-emerald-300/70">{delta}</div>}
    </div>
  )
}

function ComingSoonTile(props: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl bg-white/[0.02] p-3 text-center ring-1 ring-white/5">
      <div className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">
        Coming soon
      </div>
      <div className="mt-1.5 text-[11px] leading-tight text-white/35">{props.label}</div>
    </div>
  )
}

function TowerLogShell(props: { onHome: () => void; children: ReactNode }) {
  return (
    <div className="h-full w-full overflow-y-auto bg-slate-950 text-slate-100">
      <div className="mx-auto w-full max-w-2xl px-4 pb-16 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-wide">Tower Log</h1>
          <button
            type="button"
            onClick={props.onHome}
            className="rounded-md bg-slate-900/70 px-2.5 py-1 text-xs font-semibold text-white/80 ring-1 ring-white/10 hover:bg-slate-800/80"
          >
            ← Home
          </button>
        </div>
        <div className="space-y-4">{props.children}</div>
      </div>
    </div>
  )
}
